<script setup lang="ts">
import { useEditorStore, EditorTool } from '@/stores/editor';
import { useMapStore } from '@/stores/map';

const editorStore = useEditorStore();
const mapStore = useMapStore();

interface ToolItem {
    id: EditorTool;
    label: string;
    icon: string;
    shortcut?: string;
}

const tools: ToolItem[] = [
    { id: EditorTool.SELECT, label: 'Select', icon: '⬚', shortcut: 'V' },
    { id: EditorTool.PAINT_TILE, label: 'Paint Tile', icon: '🖌', shortcut: 'B' },
    { id: EditorTool.PAINT_OBJECT, label: 'Paint Object', icon: '🌲', shortcut: 'O' },
    { id: EditorTool.ERASE_OBJECT, label: 'Erase Object', icon: '🧹', shortcut: 'E' },
    { id: EditorTool.EYEDROPPER, label: 'Eyedropper', icon: '💧', shortcut: 'I' },
    { id: EditorTool.BUCKET_FILL, label: 'Bucket Fill', icon: '🪣', shortcut: 'G' },
    { id: EditorTool.TOGGLE_BLOCKED, label: 'Toggle Blocked', icon: '🚫', shortcut: 'X' },
    { id: EditorTool.TOGGLE_TELEPORT, label: 'Toggle Teleport', icon: '✨', shortcut: 'T' },
];

const brushSizes = [1, 2, 3, 5, 7, 10];
</script>

<template>
    <div class="toolbar">
        <!-- Tools -->
        <div class="tool-group">
            <button
                v-for="tool in tools"
                :key="tool.id"
                class="tool-btn"
                :class="{ active: editorStore.currentTool === tool.id }"
                :title="`${tool.label} (${tool.shortcut})`"
                @click="editorStore.setTool(tool.id)"
            >
                {{ tool.icon }}
            </button>
        </div>

        <div class="separator" />

        <!-- Brush size -->
        <div class="tool-group">
            <label class="tool-label">Brush:</label>
            <select
                class="brush-select"
                :value="editorStore.brushSize"
                @change="editorStore.setBrushSize(parseInt(($event.target as HTMLSelectElement).value))"
            >
                <option v-for="size in brushSizes" :key="size" :value="size">
                    {{ size }}x{{ size }}
                </option>
            </select>
        </div>

        <div class="separator" />

        <!-- Undo/Redo -->
        <div class="tool-group">
            <button
                class="tool-btn"
                :disabled="!mapStore.canUndo"
                title="Undo (Ctrl+Z)"
                @click="mapStore.undo()"
            >
                ↩
            </button>
            <button
                class="tool-btn"
                :disabled="!mapStore.canRedo"
                title="Redo (Ctrl+Y)"
                @click="mapStore.redo()"
            >
                ↪
            </button>
        </div>

        <div class="separator" />

        <!-- View toggles -->
        <div class="tool-group">
            <button
                class="tool-btn"
                :class="{ active: editorStore.showGrid }"
                title="Toggle Grid (G)"
                @click="editorStore.showGrid = !editorStore.showGrid"
            >
                #
            </button>
            <button
                class="tool-btn"
                :class="{ active: editorStore.showObjects }"
                title="Toggle Objects"
                @click="editorStore.showObjects = !editorStore.showObjects"
            >
                🌲
            </button>
        </div>

        <div class="separator" />

        <!-- Property overlays -->
        <div class="tool-group">
            <span class="tool-label">Show:</span>
            <button
                class="tool-btn property-btn blocked"
                :class="{ active: editorStore.showBlocked }"
                title="Show Blocked Tiles (Red)"
                @click="editorStore.showBlocked = !editorStore.showBlocked"
            >
                B
            </button>
            <button
                class="tool-btn property-btn teleport"
                :class="{ active: editorStore.showTeleports }"
                title="Show Teleport Tiles (Green)"
                @click="editorStore.showTeleports = !editorStore.showTeleports"
            >
                T
            </button>
            <button
                class="tool-btn property-btn farming"
                :class="{ active: editorStore.showFarming }"
                title="Show Farming Tiles (Teal)"
                @click="editorStore.showFarming = !editorStore.showFarming"
            >
                F
            </button>
            <button
                class="tool-btn property-btn buildable"
                :class="{ active: editorStore.showBuildable }"
                title="Show Buildable Tiles (Orange)"
                @click="editorStore.showBuildable = !editorStore.showBuildable"
            >
                +
            </button>
            <button
                class="tool-btn property-btn water"
                :class="{ active: editorStore.showWater }"
                title="Show Water Tiles (Blue)"
                @click="editorStore.showWater = !editorStore.showWater"
            >
                W
            </button>
        </div>

        <div class="separator" />

        <!-- Zoom -->
        <div class="tool-group">
            <button
                class="tool-btn"
                title="Zoom Out (-)"
                @click="editorStore.zoomOut()"
            >
                ➖
            </button>
            <span class="zoom-label">{{ Math.round(editorStore.zoom * 100) }}%</span>
            <button
                class="tool-btn"
                title="Zoom In (+)"
                @click="editorStore.zoomIn()"
            >
                ➕
            </button>
            <button
                class="tool-btn"
                title="Reset View"
                @click="editorStore.resetView()"
            >
                🎯
            </button>
        </div>
    </div>
</template>

<style scoped>
.toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    padding: 0 12px;
}

.tool-group {
    display: flex;
    align-items: center;
    gap: 4px;
}

.tool-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 16px;
    transition: all 0.15s ease;
}

.tool-btn:hover:not(:disabled) {
    background: var(--bg-tertiary);
    border-color: var(--border-color);
    color: var(--text-primary);
}

.tool-btn.active {
    background: var(--accent-color);
    border-color: var(--accent-color);
    color: white;
}

.tool-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.separator {
    width: 1px;
    height: 24px;
    background: var(--border-color);
    margin: 0 4px;
}

.tool-label {
    font-size: 12px;
    color: var(--text-secondary);
    margin-right: 4px;
}

.brush-select {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    color: var(--text-primary);
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
}

.zoom-label {
    font-size: 12px;
    color: var(--text-secondary);
    min-width: 45px;
    text-align: center;
}

/* Property overlay buttons with distinct colors */
.property-btn {
    font-weight: bold;
    font-size: 14px;
}

.property-btn.blocked {
    color: #ff4444;
}
.property-btn.blocked.active {
    background: #ff4444;
    border-color: #ff4444;
    color: white;
}

.property-btn.teleport {
    color: #22c55e;
}
.property-btn.teleport.active {
    background: #22c55e;
    border-color: #22c55e;
    color: white;
}

.property-btn.farming {
    color: #14b8a6;
}
.property-btn.farming.active {
    background: #14b8a6;
    border-color: #14b8a6;
    color: white;
}

.property-btn.water {
    color: #3b82f6;
}
.property-btn.water.active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: white;
}

.property-btn.buildable {
    color: #f97316;
}
.property-btn.buildable.active {
    background: #f97316;
    border-color: #f97316;
    color: white;
}
</style>
